<template>
  <div class="p-4 mx-auto space-y-4 max-w-lg">
    <!-- Region Selector -->
    <div>
      <label class="block text-sm font-medium text-gray-300"
        >Select Regions</label
      >

      <!-- Region Selection Combobox -->
      <Combobox v-model="selectedRegions" multiple>
        <div class="relative mt-2">
          <ComboboxInput
            @change="regionSearch = $event.target.value"
            placeholder="Search and select a region..."
            class="py-2 px-3 w-full text-white bg-gray-800 rounded-lg border border-gray-300 focus:outline-hidden"
            @click="isComboboxOpen = true" />

          <ComboboxButton
            class="flex absolute inset-y-0 right-0 items-center pr-2">
            <fa-icon :icon="['fas', 'search']" class="text-gray-400" />
          </ComboboxButton>
          <ComboboxOptions
            class="overflow-auto absolute z-20 mt-1 w-full max-h-60 text-white bg-gray-800 rounded-lg border shadow-lg">
            <ComboboxOption
              v-for="region in filteredRegions"
              :key="region.iso_3166_1"
              v-slot="{ active, selected }"
              :value="region.iso_3166_1"
              as="template">
              <li
                :class="[
                  active ? 'bg-blue-800' : '',
                  'cursor-pointer select-none relative py-2 px-3 flex justify-between'
                ]">
                <span>{{ region.iso_3166_1 }} - {{ region.english_name }}</span>
                <span v-if="selected" class="text-blue-600">✔</span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </div>
      </Combobox>

      <!-- Selected Regions Display -->
      <div class="flex flex-wrap gap-2 mt-2">
        <span
          v-for="regionCode in selectedRegions"
          :key="regionCode"
          class="flex items-center py-1 px-2 space-x-1 text-sm text-blue-100 bg-blue-800 rounded-sm">
          <span>{{ getRegionLabel(regionCode) }}</span>
          <button
            @click="removeRegion(regionCode)"
            class="text-blue-100 hover:text-white">
            &times;
          </button>
        </span>
        <button
          v-if="selectedRegions.length"
          @click="clearAllRegions"
          class="text-sm text-white hover:underline">
          Clear All
        </button>
      </div>
    </div>

    <!-- Providers Selection -->
    <div v-if="true">
      <h3 class="mb-2 text-lg font-semibold">
        Select Providers ({{ selectedProvidersCount }})
      </h3>

      <div class="grid grid-cols-2 gap-2">
        <button
          v-for="(provider, index) in [
            ...paginatedProviders,
            ...Array(
              Math.max(0, itemsPerPage - paginatedProviders.length)
            ).fill(null)
          ]"
          :key="provider ? provider.provider_id : `placeholder-${index}`"
          class="flex flex-col items-center py-2 px-4 w-full h-20 rounded-lg border"
          :class="
            provider
              ? isProviderSelected(provider.provider_id)
                ? 'bg-blue-800 text-white border-gray-500'
                : 'transparent text-gray-200 border-gray-500 hover:border-blue-500'
              : 'opacity-50 cursor-default'
          "
          :disabled="!provider"
          @click="provider && toggleProvider(provider.provider_id)">
          <img
            v-if="provider?.logo_path"
            :src="'https://image.tmdb.org/t/p/w45' + provider.logo_path"
            class="mb-1 w-6 h-6" />
          <span v-if="provider" class="w-full text-sm font-medium truncate">
            {{ provider.provider_name }}
          </span>
          <span v-else class="w-full text-sm font-medium text-gray-400"> </span>
          <span v-if="provider" class="text-xs italic">
            Available in: {{ getProviderCountries(provider) }}
          </span>
        </button>
      </div>

      <!-- Pagination Controls -->
      <div v-if="totalPages > 1" class="flex justify-between items-center mt-4">
        <button
          @click="prevPage"
          :disabled="currentPage === 1"
          class="flex justify-center items-center w-8 h-8 text-white bg-gray-500 rounded-full disabled:opacity-50 hover:bg-gray-700">
          <fa-icon icon="chevron-left" />
        </button>

        <span class="text-sm">Page {{ currentPage }} of {{ totalPages }}</span>

        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="flex justify-center items-center w-8 h-8 text-white bg-gray-500 rounded-full disabled:opacity-50 hover:bg-gray-700">
          <fa-icon icon="chevron-right" />
        </button>
      </div>
    </div>

    <div class="flex flex-row-reverse pt-4 border-t border-gray-700">
      <button
        v-if="!formBusy"
        type="submit"
        @click.prevent="onSubmit"
        class="flex gap-x-2 justify-between items-center py-3 px-4 w-full text-sm font-semibold text-center text-white to-indigo-500 rounded-lg border border-transparent transition ease-in-out bg-linear-to-r from-sky-500 hover:from-sky-400 hover:to-indigo-400 disabled:opacity-50 disabled:pointer-events-none">
        Save
      </button>
      <button
        v-else
        class="flex gap-x-2 justify-between items-center py-3 px-4 w-full text-sm font-semibold text-center text-white to-indigo-500 rounded-lg border border-transparent transition ease-in-out cursor-not-allowed bg-linear-to-r from-sky-500 hover:from-sky-400 hover:to-indigo-400 disabled:opacity-50 disabled:pointer-events-none">
        Updating...
      </button>
    </div>

    <div
      v-if="formError"
      @click.prevent="formError = null"
      class="p-2 mt-2 text-xs text-red-500 rounded-md cursor-pointer select-none">
      <fa-icon icon="triangle-exclamation" /> Failed to update watch providers
      <p class="font-bold">
        <span v-if="formError.message">{{ formError.message }}</span>
        <span v-else>{{ formError }}</span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useStoreTMDB } from '@/stores/storeTMDB'
import { useStoreProfile } from '@/stores/storeProfile'

import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton
} from '@headlessui/vue'

interface Provider {
  provider_id: number
  provider_name: string
  logo_path?: string
  display_priorities: Record<string, number>
}

const emit = defineEmits(['close'])
const props = defineProps({
  profile: Object
})

const storeTMDB = useStoreTMDB()
const storeProfile = useStoreProfile()

const formBusy = ref(false)
const formError = ref(null)

const regionSearch = ref('')
const isComboboxOpen = ref(false)

const selectedRegions = ref<string[]>([])
const selectedProviders = ref<number[]>([])
const currentPage = ref(1)
const itemsPerPage = 20
// Initialize form with existing profile data
onMounted(() => {
  storeTMDB.fetchWatchProvidersRegions()
  storeTMDB.fetchWatchProvidersMovies()

  if (Array.isArray(props.profile?.watch_provider_regions)) {
    selectedRegions.value = [...props.profile.watch_provider_regions]
  }

  if (Array.isArray(props.profile?.watch_provider_selections)) {
    selectedProviders.value = [...props.profile.watch_provider_selections]
  }
})

// Keep form updated if `profile` changes
watch(
  () => props.profile,
  (newProfile) => {
    if (newProfile) {
      selectedRegions.value = Array.isArray(newProfile.watch_provider_regions)
        ? [...newProfile.watch_provider_regions]
        : []

      selectedProviders.value = Array.isArray(
        newProfile.watch_provider_selections
      )
        ? [...newProfile.watch_provider_selections]
        : []
    }
  },
  { deep: true }
)

const onSubmit = (e) => {
  e.preventDefault()
  formBusy.value = true

  storeProfile
    .updateWatchProviders(selectedRegions.value, selectedProviders.value)
    .then(() => {
      formBusy.value = false
      emit('close')
    })
    .catch((error) => {
      formError.value = error
    })
    .finally(() => {
      formBusy.value = false
    })
}
// Get region label with code and name
const getRegionLabel = (code: string) => {
  const region = availableRegions.value.find((r) => r.iso_3166_1 === code)
  return region ? `${region.iso_3166_1} - ${region.english_name}` : code
}

const availableRegions = computed(() => storeTMDB.watchProvidersRegions)
const availableProvidersMovies = computed(() => storeTMDB.watchProvidersMovies)

// Get providers filtered by selected regions and sorted by priority
const filteredProviders = computed(() => {
  if (!selectedRegions.value.length) return []

  return availableProvidersMovies.value
    .filter((provider: Provider) =>
      selectedRegions.value.some(
        (region) => provider.display_priorities?.[region] !== undefined
      )
    )
    .sort((a, b) => {
      // Get the lowest priority value for each provider across selected regions
      const aPriority = Math.min(
        ...(selectedRegions.value
          .map((region) => a.display_priorities?.[region])
          .filter((priority) => priority !== undefined) as number[]) // Ensure array of numbers
      )
      const bPriority = Math.min(
        ...(selectedRegions.value
          .map((region) => b.display_priorities?.[region])
          .filter((priority) => priority !== undefined) as number[])
      )

      return aPriority - bPriority // Sort in ascending order
    })
})

// Paginated providers
const paginatedProviders = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  return filteredProviders.value.slice(start, start + itemsPerPage)
})

// Total pages for pagination
const totalPages = computed(() =>
  Math.ceil(filteredProviders.value.length / itemsPerPage)
)

// Move to previous page
const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--
}

// Move to next page
const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++
}

// Get count of selected providers
const selectedProvidersCount = computed(() => {
  return selectedProviders.value.length
})

// Check if provider is selected
const isProviderSelected = (providerId: number) => {
  return selectedProviders.value?.includes(providerId)
}

// Get provider's available countries (only selected ones)
const getProviderCountries = (provider: Provider) => {
  return Object.keys(provider.display_priorities)
    .filter((region) => selectedRegions.value.includes(region))
    .join(', ')
}

// Remove a single region
const removeRegion = (regionCode: string) => {
  selectedRegions.value = selectedRegions.value.filter(
    (code) => code !== regionCode
  )
}

// Clear all selected regions
const clearAllRegions = () => {
  selectedRegions.value = []
  selectedProviders.value = []
}

// Get regions filtered by search input
const filteredRegions = computed(() => {
  if (!regionSearch.value) return availableRegions.value
  return availableRegions.value.filter(
    (region) =>
      region.english_name
        .toLowerCase()
        .includes(regionSearch.value.toLowerCase()) ||
      region.iso_3166_1.toLowerCase().includes(regionSearch.value.toLowerCase())
  )
})

// Toggle provider selection
const toggleProvider = (providerId: number) => {
  const index = selectedProviders.value.indexOf(providerId)
  if (index === -1) {
    selectedProviders.value.push(providerId)
  } else {
    selectedProviders.value.splice(index, 1)
  }
}
</script>
